import { useEffect, useReducer, useRef, useState } from "react";
import {
  EnvioApiError,
  obterCanal,
  type Canal,
  type CanalDoCliente,
} from "@/lib/envio-colaborador-api";
import { estadoInicial, reduzir, type Estado } from "./estado";
import {
  encerrarObservabilidadeEnvio,
  iniciarObservabilidadeEnvio,
  marcarEmpresaEnvio,
  marcarPassoEnvio,
} from "@/lib/sentry-envio";
import { PassoIdentificacao } from "./components/passo-identificacao";
import { PassoAtestado } from "./components/passo-atestado";
import { Recibo } from "./components/recibo";

// Posição do estado no fluxo: cada avanço empilha uma entrada no histórico com essa
// profundidade em history.state, para o "voltar" do navegador saber para onde ir.
function profundidade(e: Estado): number {
  switch (e.passo) {
    case "identificacao":
      return 0;
    case "atestado":
      return e.tipo ? 2 : 1;
    case "recibo":
      return 3;
  }
}

function profundidadeNoHistorico(state: unknown): number {
  const n = (state as { envioProf?: unknown } | null)?.envioProf;
  return typeof n === "number" ? n : 0;
}

/** Endereço único (atestado.zavy.com.br sem empresa): o cliente só é conhecido depois da identificação. */
const UNIVERSAL = "_";
const CANAL_NEUTRO: Canal = { nome: "Envio de atestado", logoUrl: null };

export function EnvioColaboradorPage({ slug }: { slug: string }) {
  // Canal de entrada (o do endereço; no endereço único, o neutro) e o do cliente, conhecido depois da identificação.
  const [canalEntrada, setCanalEntrada] = useState<Canal | null>(null);
  const [slugEntrada, setSlugEntrada] = useState(slug);
  const [cliente, setCliente] = useState<CanalDoCliente | null>(null);
  const [erroCanal, setErroCanal] = useState<string | null>(null);
  const [estado, despachar] = useReducer(reduzir, estadoInicial);

  // Sentry: os erros do envio saem marcados com a empresa do endereço e o passo (nunca CPF/data).
  useEffect(() => {
    iniciarObservabilidadeEnvio(slug);
    return encerrarObservabilidadeEnvio;
  }, [slug]);
  useEffect(() => {
    marcarPassoEnvio(estado.passo);
  }, [estado.passo]);

  const estadoRef = useRef(estado);
  // popstate provocado pela própria página (history.go) que não deve mover o fluxo
  const ignorarPopstate = useRef(false);

  // Mantém o histórico em sincronia com a tela: avançou → empilha; recuou sem passar pelo
  // "voltar" (sessão expirada, envio já concluído, recarga) → desempilha o que sobrou, para
  // não deixar "voltar" mortos.
  useEffect(() => {
    estadoRef.current = estado;
    const prof = profundidade(estado);
    const noHistorico = profundidadeNoHistorico(window.history.state);
    if (prof > noHistorico) {
      window.history.pushState({ envioProf: prof }, "");
    } else if (prof < noHistorico && !ignorarPopstate.current) {
      ignorarPopstate.current = true;
      window.history.go(prof - noHistorico);
    }
  }, [estado]);

  useEffect(() => {
    function aoVoltar(ev: PopStateEvent) {
      if (ignorarPopstate.current) {
        ignorarPopstate.current = false;
        return;
      }
      const atual = estadoRef.current;
      const alvo = profundidadeNoHistorico(ev.state);
      if (atual.passo === "recibo") {
        window.history.pushState({ envioProf: profundidade(atual) }, "");
        return;
      }
      // "avançar" do navegador: não há tela para restaurar; desfaz o avanço
      if (alvo > profundidade(atual)) {
        ignorarPopstate.current = true;
        window.history.go(profundidade(atual) - alvo);
        return;
      }
      // recua até o alvo; o botão pode pular mais de um passo (ex.: atestado → identificação) e o
      // efeito acima desempilha a diferença
      let e = atual;
      while (profundidade(e) > alvo) {
        const anterior = reduzir(e, { t: "voltar" });
        if (anterior === e) break;
        e = anterior;
        despachar({ t: "voltar" });
      }
    }
    window.addEventListener("popstate", aoVoltar);
    return () => window.removeEventListener("popstate", aoVoltar);
  }, []);

  useEffect(() => {
    let ativo = true;
    obterCanal(slug)
      .then((c) => {
        if (ativo) setCanalEntrada(c);
      })
      .catch((e) => {
        if (!ativo) return;
        // Endereço desconhecido: segue pelo endereço único, onde o CPF + a data de nascimento apontam o cliente.
        if (e instanceof EnvioApiError && e.status === 404 && slug !== UNIVERSAL) {
          setSlugEntrada(UNIVERSAL);
          setCanalEntrada(CANAL_NEUTRO);
          return;
        }
        setErroCanal(
          e instanceof EnvioApiError ? e.message : "Link de envio indisponível.",
        );
      });
    return () => {
      ativo = false;
    };
  }, [slug]);

  if (erroCanal) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-white p-6 text-center text-slate-700">
        {erroCanal}
      </main>
    );
  }
  if (!canalEntrada) {
    return (
      <main
        className="flex min-h-dvh items-center justify-center bg-white p-6 text-slate-500"
        role="status"
      >
        Carregando…
      </main>
    );
  }

  const noInicio = estado.passo === "identificacao";
  const canal: Canal = !noInicio && cliente ? cliente : canalEntrada;
  const slugDoFluxo = !noInicio && cliente ? cliente.slug : slugEntrada;
  const voltarHistorico = () => window.history.back();
  const expirou = () => despachar({ t: "sessaoExpirada" });

  switch (estado.passo) {
    case "identificacao":
      return (
        <PassoIdentificacao
          slug={slugDoFluxo}
          canal={canal}
          aviso={estado.aviso}
          onIdentificado={(token, funcionario, doCliente) => {
            setCliente(doCliente);
            marcarEmpresaEnvio(doCliente.slug);
            despachar({ t: "identificado", token, funcionario });
          }}
        />
      );
    case "atestado":
      return (
        <PassoAtestado
          slug={slugDoFluxo}
          canal={canal}
          estado={estado}
          despachar={despachar}
          onVoltar={voltarHistorico}
          onSessaoExpirada={expirou}
        />
      );
    case "recibo":
      return (
        <Recibo
          canal={canal}
          protocolo={estado.protocolo ?? ""}
          onNovoEnvio={() => despachar({ t: "novoEnvio" })}
        />
      );
  }
}
